/**
 * ITER311 · Daily Equipment Readings — Admin Setup + Manager Fill.
 *
 * Two exports:
 *   • AdminDailyReadingsSetup — master template + apply-to-outlets.
 *   • ManagerDailyReadingsFill  — daily fill page for the outlet.
 *
 * Kept in one file to minimise import churn; both are small and share
 * shape helpers.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Send, Save, AlertTriangle, Download, FileSpreadsheet, Trash2, Pencil, Camera, X } from 'lucide-react';

const COLOR_HEX = { green: '#166534', amber: '#B45309', red: '#B91C1C', grey: '#4B5563' };

// ITER311.3 · viewport helper — mobile-first card stack under md.
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange);
    };
  }, []);
  return isMobile;
}

function Band({ title, action }) {
  return (
    <div className="rounded-lg mb-3 overflow-hidden border border-[color:var(--color-primary)]/30">
      <div className="flex items-center justify-between px-4 py-2 gap-3"
           style={{ background: 'var(--color-primary)', color: 'white' }}>
        <div className="text-sm font-semibold tracking-wide">{title}</div>
        {action}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Admin Setup
// ══════════════════════════════════════════════════════════════════
export function AdminDailyReadingsSetup() {
  const { API, getHeaders } = useAuth();
  const [master, setMaster] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [rowDialog, setRowDialog] = useState(null); // {area_id, ...}
  const [applyDialog, setApplyDialog] = useState(false);
  const [applyOutlets, setApplyOutlets] = useState([]);
  const [applyMode, setApplyMode] = useState('add_new_only');
  const [newArea, setNewArea] = useState('');
  const [newCondLabel, setNewCondLabel] = useState('');

  const load = useCallback(async () => {
    try {
      const [m, o] = await Promise.all([
        axios.get(`${API}/admin/maintenance/daily-readings/master`, { headers: getHeaders() }),
        axios.get(`${API}/outlets`, { headers: getHeaders() }),
      ]);
      setMaster(m.data);
      setOutlets(o.data || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Load failed'); }
  }, [API, getHeaders]);
  useEffect(() => { load(); }, [load]);

  if (!master) return <div className="text-sm text-[#5C6B64] py-8">Loading…</div>;

  const rowsByArea = {};
  for (const r of master.rows || []) {
    if (!r.active) continue;
    (rowsByArea[r.area_id] = rowsByArea[r.area_id] || []).push(r);
  }

  const saveRow = async (row) => {
    try {
      await axios.put(`${API}/admin/maintenance/daily-readings/master/rows`,
                       row, { headers: getHeaders() });
      // ITER311.3 · silent save — no success toast, just close & refresh.
      setRowDialog(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };
  const deleteRow = async (id) => {
    if (!confirm('Deactivate this row? History is preserved.')) return;
    try {
      await axios.delete(`${API}/admin/maintenance/daily-readings/master/rows/${id}`, { headers: getHeaders() });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };
  const addArea = async () => {
    if (!newArea.trim()) return;
    try {
      await axios.put(`${API}/admin/maintenance/daily-readings/master/areas`,
                       { name: newArea.trim() }, { headers: getHeaders() });
      setNewArea(''); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Add area failed'); }
  };
  const addCondition = async () => {
    if (!newCondLabel.trim()) return;
    try {
      await axios.put(`${API}/admin/maintenance/daily-readings/master/conditions`,
                       { label: newCondLabel.trim(), color: 'grey',
                         creates_ticket: false, priority: 'medium' },
                       { headers: getHeaders() });
      setNewCondLabel(''); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Add condition failed'); }
  };
  const applyToOutlets = async () => {
    if (!applyOutlets.length) return toast.error('Pick at least one outlet');
    if (applyMode === 'overwrite') {
      if (!confirm(`OVERWRITE will drop outlet-specific rows on ${applyOutlets.length} outlet(s). Continue?`)) return;
    }
    try {
      await axios.post(`${API}/admin/maintenance/daily-readings/master/apply`,
                        { outlet_ids: applyOutlets, mode: applyMode },
                        { headers: getHeaders() });
      // ITER311.3 · silent — legacy path, per-row chips are the new primary flow.
      setApplyDialog(false); setApplyOutlets([]);
    } catch (err) { toast.error(err.response?.data?.detail || 'Apply failed'); }
  };

  return (
    <div data-testid="admin-daily-readings-setup">
      <Band
        title="Master Rows"
        action={
          <Button size="sm" onClick={() => setRowDialog({})}
                   className="bg-white text-[color:var(--color-primary)] hover:bg-white/90"
                   data-testid="add-master-row-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add row
          </Button>
        }
      />
      {master.rows.filter(r => r.active).length === 0 ? (
        <Card className="border-dashed border-[#E2E8DF] shadow-none mb-6">
          <CardContent className="py-8 text-center text-sm text-[#5C6B64]">
            No rows configured yet. Add your first row above.
          </CardContent>
        </Card>
      ) : (
        (master.areas || []).map((area) => {
          const rows = rowsByArea[area.id] || [];
          if (!rows.length) return null;
          return (
            <div key={area.id} className="mb-4">
              <div className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold mb-2">
                {area.name} · {rows.length}
              </div>
              <Card className="border-[#E2E8DF] shadow-sm">
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F9F9F8] text-[11px] text-[#5C6B64] uppercase">
                      <tr>
                        <th className="text-left px-3 py-2">Equipment</th>
                        <th className="text-left px-3 py-2">Unit</th>
                        <th className="text-left px-3 py-2">Range</th>
                        <th className="text-left px-3 py-2">Outlets</th>
                        <th className="text-right px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const unit = (master.units || []).find(u => u.id === r.unit_id);
                        const rowOutletIds = r.outlet_ids || [];
                        const outletChips = rowOutletIds
                          .map(oid => outlets.find(o => o.id === oid))
                          .filter(Boolean);
                        return (
                          <tr key={r.id} className="border-t border-[#E2E8DF]">
                            <td className="px-3 py-2">{r.equipment_name}</td>
                            <td className="px-3 py-2 text-[#5C6B64]">{unit?.label || '—'}</td>
                            <td className="px-3 py-2 text-[#5C6B64]">
                              {r.min_value ?? '—'}–{r.max_value ?? '—'}
                            </td>
                            <td className="px-3 py-2">
                              {outletChips.length === 0 ? (
                                <span className="text-[11px] text-[#B45309] italic"
                                       data-testid={`row-outlets-empty-${r.id}`}>
                                  No outlets assigned
                                </span>
                              ) : (
                                <div className="flex flex-wrap gap-1"
                                      data-testid={`row-outlets-${r.id}`}>
                                  {outletChips.map(o => (
                                    <span key={o.id}
                                           className="inline-flex items-center px-2 py-0.5 rounded-full
                                                      bg-[#F3F5F2] text-[color:var(--color-primary)] border border-[color:var(--color-primary)]/20
                                                      text-[10px] font-medium tracking-tight">
                                      {o.name}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <Button size="sm" variant="ghost" onClick={() => setRowDialog(r)}
                                       data-testid={`edit-row-${r.id}`}>Edit</Button>
                              <Button size="sm" variant="ghost" onClick={() => deleteRow(r.id)}
                                       className="text-red-700"
                                       data-testid={`delete-row-${r.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </div>
          );
        })
      )}

      <Band title="Areas" />
      <div className="flex flex-wrap gap-2 mb-3">
        {(master.areas || []).filter(a => a.active).map(a => (
          <Badge key={a.id} className="bg-[#F3F5F2] text-[#1A2421] border-[#E2E8DF]">{a.name}</Badge>
        ))}
      </div>
      <div className="flex gap-2 mb-6">
        <Input value={newArea} onChange={(e) => setNewArea(e.target.value)}
                placeholder="New area (e.g. Terrace)" className="h-9" />
        <Button onClick={addArea} className="bg-[color:var(--color-primary)] text-white">Add</Button>
      </div>

      <Band title="Conditions" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
        {(master.conditions || []).filter(c => c.active).map(c => (
          <Card key={c.id} className="border-[#E2E8DF] shadow-none">
            <CardContent className="py-3 px-4 flex items-center justify-between">
              <div>
                <div className="font-semibold text-sm" style={{ color: COLOR_HEX[c.color] || '#1A2421' }}>● {c.label}</div>
                <div className="text-[11px] text-[#5C6B64]">
                  {c.creates_ticket ? `Creates ticket · ${c.priority}` : 'No ticket'}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="flex gap-2 mb-6">
        <Input value={newCondLabel} onChange={(e) => setNewCondLabel(e.target.value)}
                placeholder="New condition label" className="h-9" />
        <Button onClick={addCondition} className="bg-[color:var(--color-primary)] text-white">Add condition</Button>
      </div>

      <RowDialog open={!!rowDialog} row={rowDialog || {}}
                  master={master} outlets={outlets}
                  onClose={() => setRowDialog(null)}
                  onSave={saveRow} />
      <ApplyDialog open={applyDialog} outlets={outlets}
                    selected={applyOutlets} setSelected={setApplyOutlets}
                    mode={applyMode} setMode={setApplyMode}
                    onCancel={() => setApplyDialog(false)}
                    onApply={applyToOutlets} />
    </div>
  );
}

function RowDialog({ open, row, master, outlets, onClose, onSave }) {
  const [form, setForm] = useState({});
  useEffect(() => { setForm({ ...row, outlet_ids: row.outlet_ids || [] }); }, [row]);
  const submit = () => {
    if (!form.area_id || !form.equipment_name?.trim()) return toast.error('Area + equipment required');
    onSave({
      id: form.id,
      area_id: form.area_id,
      equipment_name: form.equipment_name.trim(),
      unit_id: form.unit_id || null,
      min_value: form.min_value === '' || form.min_value == null ? null : Number(form.min_value),
      max_value: form.max_value === '' || form.max_value == null ? null : Number(form.max_value),
      order: 999,
      active: true,
      outlet_ids: form.outlet_ids || [],
    });
  };
  const toggleOutlet = (oid) => {
    setForm(f => {
      const cur = new Set(f.outlet_ids || []);
      cur.has(oid) ? cur.delete(oid) : cur.add(oid);
      return { ...f, outlet_ids: Array.from(cur) };
    });
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="row-dialog">
        <DialogHeader><DialogTitle>{form.id ? 'Edit row' : 'Add row'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] uppercase text-[#5C6B64]">Area</Label>
            <Select value={form.area_id || ''} onValueChange={(v) => setForm(f => ({...f, area_id: v}))}>
              <SelectTrigger className="h-10 border-[#E2E8DF]" data-testid="row-area-select"><SelectValue placeholder="Pick area" /></SelectTrigger>
              <SelectContent>{(master.areas || []).filter(a => a.active).map(a => (
                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
              ))}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase text-[#5C6B64]">Equipment name</Label>
            <Input value={form.equipment_name || ''} onChange={(e) => setForm(f => ({...f, equipment_name: e.target.value}))}
                    className="h-10 border-[#E2E8DF]" data-testid="row-equipment-input"
                    placeholder="e.g. Backup Generator 25 kVA" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[11px] uppercase text-[#5C6B64]">Unit</Label>
              <Select value={form.unit_id || 'none'}
                       onValueChange={(v) => setForm(f => ({...f, unit_id: v === 'none' ? null : v}))}>
                <SelectTrigger className="h-10 border-[#E2E8DF]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— none —</SelectItem>
                  {(master.units || []).filter(u => u.active).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.label} ({u.name})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase text-[#5C6B64]">Min</Label>
              <Input type="number" step="any" value={form.min_value ?? ''} onChange={(e) => setForm(f => ({...f, min_value: e.target.value}))}
                      className="h-10 border-[#E2E8DF]" />
            </div>
            <div>
              <Label className="text-[11px] uppercase text-[#5C6B64]">Max</Label>
              <Input type="number" step="any" value={form.max_value ?? ''} onChange={(e) => setForm(f => ({...f, max_value: e.target.value}))}
                      className="h-10 border-[#E2E8DF]" />
            </div>
          </div>

          {/* ITER311.3 · per-row outlet scope */}
          <div>
            <Label className="text-[11px] uppercase text-[#5C6B64]">
              Assigned outlets ({(form.outlet_ids || []).length})
            </Label>
            <div className="mt-1.5 rounded-md border border-[#E2E8DF] p-2 max-h-52 overflow-y-auto"
                  data-testid="row-outlets-picker">
              {(outlets || []).length === 0 ? (
                <div className="text-xs text-[#5C6B64] italic px-1 py-2">No outlets available</div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {(outlets || []).map(o => {
                    const selected = (form.outlet_ids || []).includes(o.id);
                    return (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => toggleOutlet(o.id)}
                        data-testid={`row-outlet-chip-${o.id}`}
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium
                                    tracking-tight transition-colors border ${
                          selected
                            ? 'bg-[color:var(--color-primary)] text-white border-[color:var(--color-primary)]'
                            : 'bg-white text-[#1A2421] border-[#E2E8DF] hover:border-[color:var(--color-primary)]/60'
                        }`}
                      >
                        {selected && <span className="mr-1">✓</span>}
                        {o.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="text-[10.5px] text-[#5C6B64] mt-1 leading-relaxed">
              Row is applied only to the outlets you select. Unselecting an outlet removes this row
              from its daily register (history preserved).
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-[color:var(--color-primary)] text-white" data-testid="row-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApplyDialog({ open, outlets, selected, setSelected, mode, setMode, onCancel, onApply }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="sm:max-w-lg" data-testid="apply-dialog">
        <DialogHeader><DialogTitle>Apply master to outlets</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="max-h-64 overflow-y-auto border border-[#E2E8DF] rounded-md">
            {outlets.map(o => (
              <label key={o.id} className="flex items-center gap-2 px-3 py-2 border-b border-[#E2E8DF] cursor-pointer hover:bg-[#F9F9F8]">
                <Checkbox
                  checked={selected.includes(o.id)}
                  onCheckedChange={(c) => setSelected(prev => c ? [...prev, o.id] : prev.filter(x => x !== o.id))}
                  data-testid={`apply-outlet-${o.id}`}
                />
                <span className="text-sm">{o.name}</span>
              </label>
            ))}
          </div>
          <div>
            <Label className="text-[11px] uppercase text-[#5C6B64]">Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="h-10 border-[#E2E8DF]" data-testid="apply-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="add_new_only">Add new master rows only (safe · keeps outlet extras)</SelectItem>
                <SelectItem value="overwrite">Overwrite with master (destructive)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {mode === 'overwrite' && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded">
              <AlertTriangle className="w-4 h-4 text-red-700 mt-0.5" />
              <div className="text-xs text-red-800">
                Will drop outlet-specific rows on {selected.length} outlet(s).
                Only proceed if you truly want a clean sync.
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={onApply} className="bg-[color:var(--color-primary)] text-white" data-testid="apply-confirm-btn">
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════
// Manager Fill Tab
// ══════════════════════════════════════════════════════════════════
export function ManagerDailyReadingsFill() {
  const { API, getHeaders, user } = useAuth();
  const outletId = user?.outlet_id;
  const isMobile = useIsMobile();
  const todayIso = new Date().toISOString().slice(0, 10);
  const [selectedDate, setSelectedDate] = useState(todayIso);
  const [dat, setDat] = useState(null);
  const [master, setMaster] = useState(null);
  const [readings, setReadings] = useState({});
  const [saving, setSaving] = useState(false);
  // ITER311.4 · Amendment is per-area now — track which area is being amended.
  const [amendingAreaId, setAmendingAreaId] = useState(null);
  const [amendReason, setAmendReason] = useState('');
  const [uploadingRowId, setUploadingRowId] = useState(null);

  const load = useCallback(async () => {
    if (!outletId) return;
    try {
      const [reg, mst] = await Promise.all([
        axios.get(`${API}/admin/maintenance/daily-readings/register?outlet_id=${outletId}&date=${selectedDate}`,
                   { headers: getHeaders() }),
        axios.get(`${API}/admin/maintenance/daily-readings/master`, { headers: getHeaders() }),
      ]);
      setDat(reg.data); setMaster(mst.data);
      const map = {};
      for (const r of reg.data.entry?.rows_state || []) map[r.row_id] = r;
      setReadings(map);
      setAmendingAreaId(null); setAmendReason('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Load failed'); }
  }, [API, getHeaders, outletId, selectedDate]);
  useEffect(() => { load(); }, [load]);

  const rows = dat?.config?.rows?.filter(r => r.active) || [];
  const areasById = useMemo(() => Object.fromEntries((master?.areas || []).map(a => [a.id, a.name])), [master]);
  const unitsById = useMemo(() => Object.fromEntries((master?.units || []).map(u => [u.id, u.label])), [master]);
  const conds = (master?.conditions || []).filter(c => c.active);
  const condsById = useMemo(() => Object.fromEntries(conds.map(c => [c.id, c])), [conds]);
  // ITER311.3 · Dormant identifier — hides amber OOR + no ticket.
  const dormantCondId = useMemo(
    () => conds.find(c => (c.label || '').toLowerCase() === 'dormant')?.id || null,
    [conds],
  );
  // ITER311.4 · Map row → area for scope decisions.
  const rowToArea = useMemo(
    () => Object.fromEntries(rows.map(r => [r.id, r.area_id])),
    [rows],
  );

  const grouped = {};
  for (const r of rows) {
    const key = r.area_id;
    (grouped[key] = grouped[key] || []).push(r);
  }
  const orderedAreaIds = useMemo(() => {
    const inMaster = (master?.areas || [])
      .filter(a => a.active)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(a => a.id)
      .filter(aid => grouped[aid]);
    for (const k of Object.keys(grouped)) if (!inMaster.includes(k)) inMaster.push(k);
    return inMaster;
  }, [master, dat]);

  // Default the active area to the first configured area once loaded.

  const update = (rowId, patch) => setReadings(prev => ({
    ...prev, [rowId]: { row_id: rowId, ...(prev[rowId] || {}), ...patch }
  }));

  const uploadRowPhoto = async (rowId, file) => {
    if (!file) return;
    setUploadingRowId(rowId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/logo`, fd, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.file_url;
      if (!url) throw new Error('no url');
      const existing = (readings[rowId]?.photos) || [];
      update(rowId, { photos: [...existing, url].slice(0, 5) });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Photo upload failed');
    } finally { setUploadingRowId(null); }
  };
  const removeRowPhoto = (rowId, idx) => {
    const existing = (readings[rowId]?.photos) || [];
    update(rowId, { photos: existing.filter((_, i) => i !== idx) });
  };

  // ITER311.4 · Per-area state accessor. Reads from entry.area_states,
  // falls back to a synthetic "draft" object when no state exists yet.
  const getAreaState = (aid) => {
    const st = (dat?.entry?.area_states || []).find(a => a.area_id === aid);
    if (st) return st;
    // Legacy day-submit fallback (entry.status = "submitted" but no area_states).
    if (dat?.entry?.status === 'submitted') {
      return {
        area_id: aid, status: 'submitted',
        submitted_by_name: dat.entry.submitted_by_name || dat.entry.filled_by_name,
        submitted_at:      dat.entry.submitted_at,
        amendments:        [],
      };
    }
    return { area_id: aid, status: 'draft', amendments: [] };
  };

  // Payload builder — collect the rows for a given area from local state.
  const collectAreaRows = (aid) => Object.values(readings)
    .filter(r => (rowToArea[r.row_id] === aid) &&
                  (r.reading || r.condition_id || r.remarks || (r.photos || []).length))
    .map(r => ({ ...r, photos: r.photos || [] }));

  // ITER311.4 · Per-area save draft, submit, amend.
  const saveDraftArea = async (aid) => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/maintenance/daily-readings/register/save`,
        { outlet_id: outletId, date: selectedDate, area_id: aid,
          rows: collectAreaRows(aid) },
        { headers: getHeaders() });
      toast.success(`Draft saved · ${areasById[aid] || 'Area'}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const submitArea = async (aid) => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/maintenance/daily-readings/register/submit`,
        { outlet_id: outletId, date: selectedDate, area_id: aid,
          rows: collectAreaRows(aid) },
        { headers: getHeaders() });
      const nTix = (res.data.tickets || []).length;
      toast.success(`${areasById[aid] || 'Area'} submitted · ${nTix} ticket${nTix === 1 ? '' : 's'} created`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Submit failed'); }
    finally { setSaving(false); }
  };

  const submitAreaAmendment = async () => {
    if (!amendingAreaId) return;
    if (!amendReason.trim()) return toast.error('Reason for amendment is required');
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/maintenance/daily-readings/register/amend`,
        { outlet_id: outletId, date: selectedDate, area_id: amendingAreaId,
          reason: amendReason.trim(), rows: collectAreaRows(amendingAreaId) },
        { headers: getHeaders() });
      toast.success(res.data.permanently_locked
        ? `${areasById[amendingAreaId] || 'Area'} amendment saved — permanently locked (2/2)`
        : `${areasById[amendingAreaId] || 'Area'} amendment saved · ${res.data.amendments_used}/2 used`);
      setAmendingAreaId(null); setAmendReason('');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Amendment failed'); }
    finally { setSaving(false); }
  };

  const exportPdf = async () => {
    try {
      const res = await axios.get(
        `${API}/admin/maintenance/daily-readings/export/${outletId}/${dat.date}.pdf`,
        { headers: getHeaders(), responseType: 'blob' },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `daily-readings-${dat.date}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error('PDF export failed'); }
  };
  const exportXlsx = async () => {
    try {
      const res = await axios.get(
        `${API}/admin/maintenance/daily-readings/export/${outletId}/${dat.date}.xlsx`,
        { headers: getHeaders(), responseType: 'blob' },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `daily-readings-${dat.date}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error('XLSX export failed'); }
  };

  // ITER311.3 · skeleton loader instead of "Loading…" text.
  if (!dat) return <FillSkeleton />;
  if (rows.length === 0) return (
    <Card className="border-dashed border-[#E2E8DF] shadow-none">
      <CardContent className="py-10 text-center text-sm text-[#5C6B64]">
        No readings configured for this outlet yet. Ask your admin to assign this outlet on the master rows.
      </CardContent>
    </Card>
  );

  const daysAgo = Math.floor((new Date(todayIso) - new Date(selectedDate)) / 86400000);
  const isEditableDate = daysAgo === 0 || daysAgo === 1;
  const dayStatus = dat.entry?.status || 'draft';
  const anySubmitted = (dat.entry?.area_states || []).some(a => a.status === 'submitted')
                        || dayStatus === 'submitted';

  const isAreaSubmitted = (aid) => getAreaState(aid).status === 'submitted';
  const areaAmendmentsUsed = (aid) => (getAreaState(aid).amendments || []).length;
  const isAreaPermLocked = (aid) => isAreaSubmitted(aid) && areaAmendmentsUsed(aid) >= 2;
  const canAmendArea = (aid) => isAreaSubmitted(aid) && isEditableDate && areaAmendmentsUsed(aid) < 2;
  const isAreaDisabled = (aid) => {
    if (!isEditableDate) return true;
    if (!isAreaSubmitted(aid)) return false;
    return amendingAreaId !== aid;
  };

  const computeRowState = (r) => {
    const rs = readings[r.id] || {};
    const val = rs.reading;
    const cond = condsById[rs.condition_id];
    const isDormant = rs.condition_id && rs.condition_id === dormantCondId;
    let oor = false;
    const num = Number(val);
    if (!isDormant && val !== '' && val != null && !isNaN(num)) {
      if (r.min_value != null && num < r.min_value) oor = true;
      if (r.max_value != null && num > r.max_value) oor = true;
    }
    const uom = unitsById[r.unit_id] || '';
    return { rs, val, cond, isDormant, oor, uom };
  };

  return (
    // ITER311.4-fix#2 · true full-bleed on mobile.
    // `main` has `px-3` (12px). Using `-mx-3` alone only SHIFTS the element,
    // it does not EXTEND its width — so we ended up 366px wide inside a
    // 390px viewport (24px right gutter). Fix: `w-screen` claims the true
    // viewport width and `-ml-3` slides it back to hug the left edge.
    // Desktop (sm+) reverts to a normal in-flow full-width block.
    <div data-testid="daily-readings-fill" className="w-screen -ml-3 sm:w-full sm:ml-0 sm:mx-0">
      <Band
        title={`Daily Readings · ${dat.date}`}
        action={
          <div className="flex gap-2 items-center text-white text-xs flex-wrap">
            <Input type="date" value={selectedDate}
                    max={todayIso}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="h-8 w-36 sm:w-40 bg-white text-[#1A2421] text-xs"
                    data-testid="readings-date-picker" />
            {dat.entry && !isMobile && (
              <span data-testid="day-status-stamp">
                {dayStatus === 'submitted' ? 'All areas submitted' :
                 dayStatus === 'partial'   ? `Partial · ${(dat.entry.area_states || []).filter(a => a.status === 'submitted').length}/${orderedAreaIds.length}` :
                 'Draft in progress'}
              </span>
            )}
            {anySubmitted && (
              <>
                <Button size="sm" onClick={exportPdf}
                         className="bg-white text-[color:var(--color-primary)] hover:bg-white/90"
                         data-testid="export-readings-pdf-btn">
                  <Download className="w-3.5 h-3.5 mr-1" /> PDF
                </Button>
                <Button size="sm" onClick={exportXlsx}
                         className="bg-white text-[color:var(--color-primary)] hover:bg-white/90"
                         data-testid="export-readings-xlsx-btn">
                  <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel
                </Button>
              </>
            )}
          </div>
        }
      />

      {!isEditableDate && (
        <div className="mt-3 mx-4 sm:mx-0 p-3 rounded bg-slate-100 border border-slate-300 text-xs text-slate-700"
              data-testid="readonly-past-strip">
          Read only — this date is older than yesterday. Only today&apos;s and yesterday&apos;s registers can be filled.
        </div>
      )}

      {/* One section per area. Each section owns its Submit button INLINE at
          the end of its cards — no sticky action bar, no IntersectionObserver.
          The submit button is replaced by a Locked stamp once the area is
          submitted, so each area is a self-contained log-book entry. */}
      {orderedAreaIds.map((areaId) => {
        const areaRows = grouped[areaId] || [];
        const areaName = areasById[areaId] || 'Uncategorised';
        const st = getAreaState(areaId);
        const submittedHere = st.status === 'submitted';
        const disabledHere  = isAreaDisabled(areaId);
        const amendmentsHere = (st.amendments || []).length;
        const permLockedHere = submittedHere && amendmentsHere >= 2;
        const amendingHere = amendingAreaId === areaId;
        const submittedAt = (st.submitted_at || '').slice(11, 16);
        return (
          <section key={areaId}
                    data-area-id={areaId}
                    data-testid={`area-section-${areaId}`}
                    className="mb-4 scroll-mt-16">
            {/* Sticky area sub-header — full-bleed. Uses flex-wrap so the
                stamp wraps below the label on narrow phones instead of
                truncating the area name. */}
            <div
              className="sticky top-0 z-10 px-4 py-2 text-white bg-[color:var(--color-primary)]
                          border-b border-[color:var(--color-primary-active)]/40 flex flex-wrap items-center gap-x-3 gap-y-1"
              data-testid={`area-header-${areaId}`}
            >
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] whitespace-nowrap">
                {areaName} · {areaRows.length}
              </div>
              {submittedHere ? (
                <div className="text-[10.5px] opacity-90 flex items-center gap-1.5 flex-wrap"
                      data-testid={`area-stamp-${areaId}`}>
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-300 shrink-0" />
                  <span>Locked · Filled by <b>{st.submitted_by_name || '—'}</b>
                    {submittedAt ? ` at ${submittedAt}` : ''}
                    {' · '}Amendments {amendmentsHere}/2
                    {permLockedHere && ' · permanently'}
                  </span>
                </div>
              ) : (
                <div className="text-[10.5px] opacity-90 flex items-center gap-1.5"
                      data-testid={`area-draft-${areaId}`}>
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-white/70" />
                  Draft
                </div>
              )}
            </div>

            {amendingHere && (
              <div className="p-3 mx-4 sm:mx-0 my-2 rounded bg-amber-50 border border-amber-200"
                    data-testid={`amend-panel-${areaId}`}>
                <div className="text-xs font-semibold text-amber-900 mb-2">
                  {areaName} · Amendment {amendmentsHere + 1} of 2 — reason is required
                </div>
                <Textarea rows={2} value={amendReason}
                           onChange={(e) => setAmendReason(e.target.value)}
                           placeholder="Why is this amendment needed?"
                           className="border-amber-300 bg-white"
                           data-testid={`amend-reason-input-${areaId}`} />
              </div>
            )}

            {isMobile ? (
              <div className={`w-full ${submittedHere && !amendingHere ? 'bg-[#F3F5F2]' : 'bg-[#F9F9F8]'} border-b border-[#E2E8DF]`}>
                {areaRows.map((r) => (
                  <ReadingCard
                    key={r.id} row={r} state={computeRowState(r)}
                    conditions={conds} inputsDisabled={disabledHere}
                    onChange={(patch) => update(r.id, patch)}
                    onUploadPhoto={(f) => uploadRowPhoto(r.id, f)}
                    onRemovePhoto={(i) => removeRowPhoto(r.id, i)}
                    uploading={uploadingRowId === r.id}
                  />
                ))}
              </div>
            ) : (
              <Card className={`border-[#E2E8DF] shadow-sm rounded-none border-t-0 ${submittedHere && !amendingHere ? 'bg-[#F3F5F2]' : ''}`}>
                <CardContent className="p-0">
                  <table className="w-full text-sm table-fixed">
                    <colgroup>
                      <col className="w-[38%]" />
                      <col className="w-[22%]" />
                      <col className="w-[18%]" />
                      <col className="w-[22%]" />
                    </colgroup>
                    <thead className="bg-[#F9F9F8] text-[11px] text-[#5C6B64] uppercase">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">Equipment</th>
                        <th className="text-left px-3 py-2 font-semibold">Reading</th>
                        <th className="text-left px-3 py-2 font-semibold">Condition</th>
                        <th className="text-left px-3 py-2 font-semibold">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {areaRows.map((r) => {
                        const { rs, val, cond, oor, uom } = computeRowState(r);
                        return (
                          <tr key={r.id} className="border-t border-[#E2E8DF]">
                            <td className="px-3 py-2 align-middle">
                              <div className="text-sm leading-snug break-words">
                                {r.equipment_name}
                              </div>
                              {(r.min_value != null || r.max_value != null) && (
                                <div className="text-[10px] text-[#5C6B64] mt-0.5">
                                  Range {r.min_value ?? '—'}–{r.max_value ?? '—'}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 align-middle">
                              <div className="relative">
                                <Input
                                  disabled={disabledHere}
                                  inputMode="decimal"
                                  value={val ?? ''}
                                  onChange={(e) => update(r.id, { reading: e.target.value })}
                                  className={`h-11 w-full border-[#E2E8DF] text-sm px-3 ${uom ? 'pr-12' : ''} ${oor ? 'bg-amber-50 border-amber-400' : ''}`}
                                  data-testid={`reading-input-${r.id}`}
                                />
                                {uom && (
                                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[11px] font-medium text-[#5C6B64]">
                                    {uom}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2 align-middle">
                              <Select value={rs.condition_id || ''} disabled={disabledHere}
                                       onValueChange={(v) => update(r.id, { condition_id: v })}>
                                <SelectTrigger className="h-11 w-full border-[#E2E8DF] text-sm px-3"
                                                data-testid={`cond-select-${r.id}`}>
                                  <SelectValue placeholder="—" />
                                </SelectTrigger>
                                <SelectContent>
                                  {conds.map(c => (
                                    <SelectItem key={c.id} value={c.id}>
                                      <span style={{ color: COLOR_HEX[c.color] }}>● {c.label}</span>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {cond?.creates_ticket && !rs.remarks && (
                                <div className="text-[10px] text-red-700 mt-1">Remarks required</div>
                              )}
                            </td>
                            <td className="px-3 py-2 align-middle">
                              <Input disabled={disabledHere} value={rs.remarks || ''}
                                      onChange={(e) => update(r.id, { remarks: e.target.value })}
                                      className="h-11 w-full border-[#E2E8DF] text-sm px-3"
                                      placeholder="Optional" />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* ── ITER311.4-fix · Inline per-section action row ── */}
            {isEditableDate && (
              <div className={`px-4 sm:px-0 py-3 flex flex-wrap gap-2 items-center justify-end
                                border-b border-[#E2E8DF] ${submittedHere && !amendingHere ? 'bg-[#F3F5F2]' : 'bg-white'}`}
                    data-testid={`area-actions-${areaId}`}>
                {!submittedHere && !amendingHere && (
                  <>
                    <Button variant="outline" disabled={saving}
                            onClick={() => saveDraftArea(areaId)}
                            className="border-[#E2E8DF] h-11 flex-1 sm:flex-none sm:w-auto"
                            data-testid={`save-draft-btn-${areaId}`}>
                      <Save className="w-4 h-4 mr-1" /> Save draft
                    </Button>
                    <Button disabled={saving} onClick={() => submitArea(areaId)}
                            className="bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] h-11 flex-[2] sm:flex-none sm:w-auto"
                            data-testid={`submit-area-btn-${areaId}`}>
                      <Send className="w-4 h-4 mr-1" /> Submit {areaName}
                    </Button>
                  </>
                )}
                {submittedHere && !amendingHere && canAmendArea(areaId) && (
                  <Button onClick={() => { setAmendingAreaId(areaId); setAmendReason(''); }}
                          className="bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] h-11 flex-1 sm:flex-none sm:w-auto"
                          data-testid={`amend-area-btn-${areaId}`}>
                    <Pencil className="w-4 h-4 mr-1" /> Amend {areaName}
                  </Button>
                )}
                {submittedHere && !amendingHere && !canAmendArea(areaId) && (
                  <div className="text-[11px] text-slate-600 italic"
                        data-testid={`area-permlocked-${areaId}`}>
                    {permLockedHere
                      ? `${areaName} permanently locked · 2/2 amendments used`
                      : `${areaName} locked (older than yesterday)`}
                  </div>
                )}
                {amendingHere && (
                  <>
                    <Button variant="outline" disabled={saving}
                            onClick={() => { setAmendingAreaId(null); setAmendReason(''); load(); }}
                            className="border-[#E2E8DF] h-11 flex-1 sm:flex-none sm:w-auto"
                            data-testid={`amend-cancel-btn-${areaId}`}>
                      Cancel
                    </Button>
                    <Button disabled={saving || !amendReason.trim()} onClick={submitAreaAmendment}
                            className="bg-[#B45309] text-white hover:bg-[#9a4508] h-11 flex-[2] sm:flex-none sm:w-auto"
                            data-testid={`amend-save-btn-${areaId}`}>
                      <Save className="w-4 h-4 mr-1" />
                      Save amendment ({amendmentsHere + 1}/2)
                    </Button>
                  </>
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

// ── Mobile Card for a single reading row ─────────────────────────────
function ReadingCard({ row, state, conditions, inputsDisabled,
                       onChange, onUploadPhoto, onRemovePhoto, uploading }) {
  const { rs, val, cond, oor, uom } = state;
  const photos = rs.photos || [];
  const showPhotoAffordance = cond?.creates_ticket === true;
  return (
    <div
      className={`px-3 py-3 border-b border-[#E2E8DF] bg-white ${oor ? 'border-l-4 border-l-amber-500' : ''}`}
      data-testid={`reading-card-${row.id}`}
    >
      {/* Equipment name — its own line, readable. */}
      <div className="text-[15px] font-semibold text-[#1A2421] leading-snug break-words">
        {row.equipment_name}
      </div>
      {(row.min_value != null || row.max_value != null) && (
        <div className="text-[11px] text-[#5C6B64] mt-0.5 mb-2">
          Expected {row.min_value ?? '—'}–{row.max_value ?? '—'} {uom}
        </div>
      )}

      {/* Reading — full width with UOM inline. Native numeric keypad. */}
      <div className="mt-2">
        <label className="block text-[10px] uppercase tracking-wider text-[#5C6B64] mb-1 font-semibold">
          Reading
        </label>
        <div className="relative">
          <input
            type="text"
            inputMode="decimal"
            disabled={inputsDisabled}
            value={val ?? ''}
            onChange={(e) => onChange({ reading: e.target.value })}
            placeholder="—"
            className={`w-full h-12 rounded-md border text-[15px] px-3 ${uom ? 'pr-14' : ''}
                        ${oor ? 'border-amber-400 bg-amber-50' : 'border-[#E2E8DF] bg-white'}
                        focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)]/30 focus:border-[color:var(--color-primary)]
                        disabled:bg-[#F3F5F2] disabled:text-[#5C6B64]`}
            data-testid={`reading-input-${row.id}`}
          />
          {uom && (
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[12px] font-semibold text-[#5C6B64]">
              {uom}
            </span>
          )}
        </div>
      </div>

      {/* Condition — compact shadcn dropdown, matches P&L Month selector style.
          Radix Popover renders a light, positioned menu that closes on tap-outside
          and on selection — no full-screen dialog, no dark overlay. */}
      <div className="mt-3">
        <label className="block text-[10px] uppercase tracking-wider text-[#5C6B64] mb-1 font-semibold">
          Condition
        </label>
        <Select value={rs.condition_id || ''} disabled={inputsDisabled}
                 onValueChange={(v) => onChange({ condition_id: v || null })}>
          <SelectTrigger
            className={`h-11 w-full text-[14px] bg-white border-[#E2E8DF] px-3
                        disabled:bg-[#F3F5F2] disabled:text-[#5C6B64]`}
            data-testid={`cond-select-${row.id}`}
          >
            <SelectValue placeholder="— select condition —" />
          </SelectTrigger>
          <SelectContent
            className="min-w-[var(--radix-select-trigger-width)] w-[var(--radix-select-trigger-width)]
                        max-h-[240px] text-[14px] shadow-md border-[#E2E8DF]"
            position="popper" sideOffset={4}
          >
            {conditions.map(c => (
              <SelectItem key={c.id} value={c.id} className="text-[14px] py-2">
                <span className="inline-flex items-center gap-2">
                  <span style={{ color: COLOR_HEX[c.color] || '#1A2421' }}>●</span>
                  {c.label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {cond && (
          <div className="flex items-center gap-1 mt-1 text-[11px] font-medium"
                style={{ color: COLOR_HEX[cond.color] || '#1A2421' }}>
            <span>●</span> <span>{cond.label}</span>
            {cond.creates_ticket && !rs.remarks && (
              <span className="text-red-700 ml-2">· remarks required</span>
            )}
          </div>
        )}
      </div>

      {/* Remarks — full width. */}
      <div className="mt-3">
        <label className="block text-[10px] uppercase tracking-wider text-[#5C6B64] mb-1 font-semibold">
          Remarks {cond?.creates_ticket ? <span className="text-red-700">*</span> : <span className="text-[#5C6B64] normal-case">(optional)</span>}
        </label>
        <input
          type="text"
          disabled={inputsDisabled}
          value={rs.remarks || ''}
          onChange={(e) => onChange({ remarks: e.target.value })}
          placeholder={cond?.creates_ticket ? 'What is the issue?' : 'Anything to note?'}
          className={`w-full h-12 rounded-md border border-[#E2E8DF] text-[15px] px-3 bg-white
                      focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)]/30 focus:border-[color:var(--color-primary)]
                      disabled:bg-[#F3F5F2] disabled:text-[#5C6B64]`}
          data-testid={`remarks-input-${row.id}`}
        />
      </div>

      {/* Photo affordance — only when the selected condition creates a ticket. */}
      {showPhotoAffordance && (
        <div className="mt-3">
          <label className="block text-[10px] uppercase tracking-wider text-[#5C6B64] mb-1 font-semibold">
            Photos ({photos.length}/5)
          </label>
          <div className="flex flex-wrap gap-2">
            {photos.map((url, i) => (
              <div key={i} className="relative w-16 h-16 rounded border border-[#E2E8DF] overflow-hidden">
                <img src={url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                {!inputsDisabled && (
                  <button
                    type="button"
                    onClick={() => onRemovePhoto(i)}
                    className="absolute top-0 right-0 bg-black/70 text-white text-[11px] w-5 h-5 flex items-center justify-center rounded-bl"
                    data-testid={`remove-photo-${row.id}-${i}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
            {!inputsDisabled && photos.length < 5 && (
              <label
                className={`w-16 h-16 flex flex-col items-center justify-center rounded border border-dashed
                            border-[color:var(--color-primary)]/40 bg-[#F3F5F2] cursor-pointer hover:bg-[#E9EFEB]
                            transition-colors text-[10px] text-[color:var(--color-primary)] font-semibold`}
                data-testid={`add-photo-${row.id}`}
              >
                {uploading ? (
                  <span className="text-[10px]">…</span>
                ) : (
                  <>
                    <Camera className="w-5 h-5 mb-0.5" />
                    <span>Photo</span>
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => onUploadPhoto(e.target.files?.[0])}
                  data-testid={`photo-input-${row.id}`}
                />
              </label>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Skeleton loader shown while the register + master boot. ──────────
function FillSkeleton() {
  return (
    <div data-testid="daily-readings-skeleton" className="animate-pulse">
      <div className="h-10 bg-[color:var(--color-primary)]/60 rounded-lg mb-3" />
      <div className="h-6 bg-[color:var(--color-primary)]/40 mb-2" />
      <div className="space-y-2 px-3 sm:px-0">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="h-24 bg-[#F3F5F2] rounded-md border border-[#E2E8DF]" />
        ))}
      </div>
    </div>
  );
}

export default AdminDailyReadingsSetup;
